import { useState, useRef } from "react";
import { auth } from "../firebase";
import { C } from "../constants/colors";
import { MONTH_ABBR, streamOptionsWithLegacy, wasteMetricOf, wasteMetricUnitLabel } from "../constants/wasteData";
import { sum, fmtMoney, fmtMetric } from "../utils/format";
import { Card, Btn, SectionTag, TriBtn, labelStyle, inputStyle } from "../components/ui";
import { LineChart, BarChart } from "../components/charts";

const LINE_ITEM_TYPES=[["regular_service","Regular Service"],["fee","Fee"],["special_pickup","Special Pickup"],["unknown","Unknown"]];
const currentMonth=()=>{const d=new Date();return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;};

export default function WasteDataHub({waste,accounts,org,setScreen,setDataConfigOrigin}){
  const {
    wasteData,setWasteData,wasteDataConfig,
    wasteUploadErrors,wasteUploadSuccess,wasteUploading,
    wasteFileRef,handleWasteUpload,
  }=waste;
  const {wasteAccounts,addLineItemMappings}=accounts;
  const [selectedWasteYear,setSelectedWasteYear]=useState(null);

  // ── Client: invoice upload → account line-item matching → historical data ──
  const [selectedAccountId,setSelectedAccountId]=useState("");
  const [invoiceUploading,setInvoiceUploading]=useState(false);
  const [invoiceError,setInvoiceError]=useState("");
  const [invoicePeriod,setInvoicePeriod]=useState("");
  const [reviewItems,setReviewItems]=useState(null); // null = not reviewing; array = post-extraction review/classification
  const invoiceFileRef=useRef();

  // Builds the historical wasteData record(s) from reviewed line items, grouped by
  // stream, using the account's declared invoiceFormat — never inventing a volume or
  // weight value that wasn't actually extracted from the invoice.
  const commitHistoricalData=(account,items,rawDate)=>{
    const date=/^\d{4}-(0[1-9]|1[0-2])$/.test(rawDate)?rawDate:currentMonth();
    const byStream={};
    items.forEach(item=>{
      const s=item.stream||account.stream;
      if(!byStream[s]) byStream[s]={cost:null,volume:null,weightTons:null,weightLbs:null};
      if(typeof item.amount==="number") byStream[s].cost=(byStream[s].cost||0)+item.amount;
      if(account.invoiceFormat.hasVolume&&typeof item.volumeCubicYards==="number"){
        byStream[s].volume=(byStream[s].volume||0)+item.volumeCubicYards;
      }
      if(account.invoiceFormat.hasWeight&&account.invoiceFormat.weightUnit==="tons"&&typeof item.weightTons==="number"){
        byStream[s].weightTons=(byStream[s].weightTons||0)+item.weightTons;
      }
      if(account.invoiceFormat.hasWeight&&account.invoiceFormat.weightUnit==="lbs"&&typeof item.weightLbs==="number"){
        byStream[s].weightLbs=(byStream[s].weightLbs||0)+item.weightLbs;
      }
    });
    const records=Object.entries(byStream).map(([streamKey,v])=>({
      date,locationName:account.name,stream:streamKey,
      volume:account.invoiceFormat.hasVolume?v.volume:null,
      weightTons:account.invoiceFormat.hasWeight&&account.invoiceFormat.weightUnit==="tons"?v.weightTons:null,
      weightLbs:account.invoiceFormat.hasWeight&&account.invoiceFormat.weightUnit==="lbs"?v.weightLbs:null,
      costDollars:v.cost,
    }));
    setWasteData(prev=>[...prev,...records]);
  };

  const handleInvoiceFile=async(file)=>{
    const account=wasteAccounts.find(a=>a.id===selectedAccountId);
    if(!account) return;
    if(file.type!=="application/pdf"){setInvoiceError("Please upload a PDF invoice.");return;}
    setInvoiceUploading(true);setInvoiceError("");
    try{
      const b64=await new Promise((res,rej)=>{const r=new FileReader();r.onload=()=>res(r.result.split(",")[1]);r.onerror=rej;r.readAsDataURL(file);});
      const messages=[{role:"user",content:[{type:"document",source:{type:"base64",media_type:"application/pdf",data:b64}},{type:"text",text:"Extract line items from this waste hauling invoice. Return ONLY valid JSON as instructed."}]}];
      const sys=`You extract individual charge line items from a waste hauling invoice for historical record-keeping. Return ONLY valid JSON, no markdown:
{"invoiceDate":"YYYY-MM or null","lineItems":[{"rawLabel":"string copied exactly as printed on the invoice","amount":number or null,"volumeCubicYards":number or null,"weightTons":number or null,"weightLbs":number or null}]}
Rules: rawLabel must be verbatim invoice text, never paraphrased or summarized. Only include a numeric value if it is explicitly printed on the invoice — use null instead of estimating or inferring. Include every distinct charge line (regular service, fees, surcharges, special pickups) as its own item.`;
      const token=await auth.currentUser.getIdToken();
      const res=await fetch(`${process.env.REACT_APP_API_URL || "http://localhost:3001"}/api/claude`,{method:"POST",headers:{"Content-Type":"application/json","Authorization":`Bearer ${token}`},body:JSON.stringify({model:"claude-sonnet-4-6",max_tokens:1200,system:sys,messages})});
      const data=await res.json();
      const clean=(data.content?.[0]?.text||"").replace(/```json|```/g,"").trim();
      const parsed=JSON.parse(clean);
      const items=(parsed.lineItems||[]).map(item=>{
        const rawLabel=(item.rawLabel||"").trim();
        const mapping=(account.lineItemMappings||[]).find(m=>m.rawLabel.trim()===rawLabel);
        return{
          rawLabel,
          amount:typeof item.amount==="number"?item.amount:null,
          volumeCubicYards:typeof item.volumeCubicYards==="number"?item.volumeCubicYards:null,
          weightTons:typeof item.weightTons==="number"?item.weightTons:null,
          weightLbs:typeof item.weightLbs==="number"?item.weightLbs:null,
          type:mapping?mapping.type:"unknown",
          stream:mapping?mapping.stream:account.stream,
          isNew:!mapping,
        };
      });
      setInvoicePeriod(/^\d{4}-(0[1-9]|1[0-2])$/.test(parsed.invoiceDate)?parsed.invoiceDate:currentMonth());
      setReviewItems(items);
    }catch(e){
      console.error("handleInvoiceFile",e);
      setInvoiceError("Could not read that invoice. Try a different file, or ask your admin to add this data via the historical CSV upload below.");
    }
    setInvoiceUploading(false);
  };

  const updateReviewItem=(idx,field,val)=>{
    setReviewItems(prev=>prev.map((it,i)=>i===idx?{...it,[field]:val}:it));
  };
  const cancelReview=()=>{ setReviewItems(null); setInvoiceError(""); setInvoicePeriod(""); };
  const finishReview=()=>{
    const account=wasteAccounts.find(a=>a.id===selectedAccountId);
    if(!account||!reviewItems){cancelReview();return;}
    const newMappings=reviewItems.filter(it=>it.isNew).map(it=>({rawLabel:it.rawLabel,type:it.type,stream:it.stream}));
    addLineItemMappings(account.id,newMappings);
    commitHistoricalData(account,reviewItems,invoicePeriod);
    cancelReview();
  };

  // ── Waste Data Hub aggregation (fiscal-year view of historical data) ────
  const wasteYears=[...new Set(wasteData.map(r=>r.date.slice(0,4)))].sort();
  const activeWasteYear=selectedWasteYear||wasteYears[wasteYears.length-1]||null;
  const wasteHasMetric=!!(wasteDataConfig.hasVolume||wasteDataConfig.hasWeight);
  const wasteMetricLabel=wasteDataConfig.hasVolume?"Volume":wasteDataConfig.hasWeight?"Weight":"";
  const wasteMetricUnit=wasteMetricUnitLabel(wasteDataConfig);
  const wasteRecordsForYear=activeWasteYear?wasteData.filter(r=>r.date.startsWith(activeWasteYear)):[];
  const wasteMonthly=Array.from({length:12},(_,i)=>{
    const mm=String(i+1).padStart(2,"0");
    const monthRecords=wasteRecordsForYear.filter(r=>r.date.slice(5,7)===mm);
    return{
      label:MONTH_ABBR[i],
      cost:sum(monthRecords.map(r=>r.costDollars||0)),
      metric:sum(monthRecords.map(r=>wasteMetricOf(r,wasteDataConfig)||0)),
    };
  });
  const wasteTotalCostYear=sum(wasteRecordsForYear.map(r=>r.costDollars||0));
  const wasteTotalMetricYear=sum(wasteRecordsForYear.map(r=>wasteMetricOf(r,wasteDataConfig)||0));
  const prevWasteYear=activeWasteYear?String(Number(activeWasteYear)-1):null;
  const wasteRecordsPrevYear=prevWasteYear?wasteData.filter(r=>r.date.startsWith(prevWasteYear)):[];
  const wasteTotalCostPrevYear=sum(wasteRecordsPrevYear.map(r=>r.costDollars||0));
  const wasteYoyChange=wasteTotalCostPrevYear>0?((wasteTotalCostYear-wasteTotalCostPrevYear)/wasteTotalCostPrevYear)*100:null;
  const wasteByLocation={};
  wasteRecordsForYear.forEach(r=>{
    if(!wasteByLocation[r.locationName]) wasteByLocation[r.locationName]={cost:0,metric:0};
    wasteByLocation[r.locationName].cost+=r.costDollars||0;
    wasteByLocation[r.locationName].metric+=wasteMetricOf(r,wasteDataConfig)||0;
  });
  const wasteLocationRows=Object.entries(wasteByLocation).map(([name,v])=>({name,...v})).sort((a,b)=>b.metric-a.metric);
  const topWasteLocation=wasteLocationRows[0]||null;

  const openConfig=()=>{setDataConfigOrigin("hub");setScreen("data-config");};

  return(
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20,flexWrap:"wrap",gap:12}}>
        <div>
          <SectionTag label="Waste Data Hub"/>
          <h2 style={{fontSize:22,fontWeight:800,color:C.navy,margin:"10px 0 4px"}}>Historical Waste & Cost Data</h2>
          <p style={{color:C.muted,fontSize:13}}>{org.name} · Monthly hauler data across all locations</p>
        </div>
        <div style={{display:"flex",gap:8,flexShrink:0,alignItems:"flex-start"}}>
          <Btn variant="ghost" onClick={()=>setScreen("waste-accounts")} style={{fontSize:12,padding:"9px 14px"}}>⚙ Waste Accounts</Btn>
          <Btn variant="ghost" onClick={openConfig} style={{fontSize:12,padding:"9px 14px"}}>Unit Settings</Btn>
        </div>
      </div>

      {/* ── Client: Upload Invoice (prominent) ─────────────────── */}
      <Card style={{marginBottom:16,borderLeft:`4px solid ${C.teal}`}}>
        <div style={{fontWeight:700,color:C.navy,fontSize:16,marginBottom:4}}>📄 Upload Invoice</div>
        <p style={{fontSize:13,color:C.muted,marginBottom:16,lineHeight:1.5}}>Upload a hauler invoice as a PDF and we'll match its charges to a waste account, flag anything new for you to classify, and add it to your historical data.</p>

        {reviewItems?(
          <div>
            {(()=>{
              const account=wasteAccounts.find(a=>a.id===selectedAccountId);
              const newItems=reviewItems.filter(it=>it.isNew);
              const knownItems=reviewItems.filter(it=>!it.isNew);
              return(
                <>
                  <p style={{fontSize:13,color:C.muted,marginBottom:16,lineHeight:1.6}}>
                    Extracted for <strong style={{color:C.navy}}>{account?.name}</strong>. {newItems.length>0?`${newItems.length} line item${newItems.length!==1?"s":""} weren't recognized — classify them below so future invoices from this account are matched automatically.`:"Every line item matched a known mapping for this account."}
                  </p>

                  <div style={{marginBottom:14}}>
                    <label style={labelStyle}>Billing Period</label>
                    <input type="month" style={{...inputStyle,width:200}} value={invoicePeriod} onChange={e=>setInvoicePeriod(e.target.value)}/>
                  </div>

                  {newItems.length>0&&(
                    <div style={{marginBottom:14}}>
                      <div style={{fontWeight:700,color:C.navy,fontSize:13,marginBottom:8}}>Unrecognized Line Items</div>
                      {reviewItems.map((item,i)=>item.isNew&&(
                        <div key={i} style={{background:C.sand,borderRadius:10,padding:"14px 16px",marginBottom:10}}>
                          <div style={{fontWeight:600,color:C.navy,fontSize:13,marginBottom:2}}>{item.rawLabel||"(no label extracted)"}</div>
                          <div style={{fontSize:11,color:C.muted,marginBottom:10}}>
                            {item.amount!=null?`$${item.amount.toLocaleString()}`:"no amount"}
                            {item.volumeCubicYards!=null?` · ${item.volumeCubicYards} cu yd`:""}
                            {item.weightTons!=null?` · ${item.weightTons} tons`:""}
                            {item.weightLbs!=null?` · ${item.weightLbs} lbs`:""}
                          </div>
                          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                            <div>
                              <div style={{fontSize:11,fontWeight:700,color:C.muted,marginBottom:4}}>TYPE</div>
                              <TriBtn options={LINE_ITEM_TYPES} value={item.type} onChange={v=>updateReviewItem(i,"type",v)}/>
                            </div>
                            <div>
                              <div style={{fontSize:11,fontWeight:700,color:C.muted,marginBottom:4}}>STREAM</div>
                              <select style={{...inputStyle,fontSize:12}} value={item.stream} onChange={e=>updateReviewItem(i,"stream",e.target.value)}>
                                {streamOptionsWithLegacy(item.stream).map(s=><option key={s} value={s}>{s}</option>)}
                              </select>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {knownItems.length>0&&(
                    <div style={{marginBottom:16}}>
                      <div style={{fontWeight:700,color:C.navy,fontSize:13,marginBottom:8}}>Recognized Line Items</div>
                      {knownItems.map((item,i)=>(
                        <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 0",borderBottom:i<knownItems.length-1?`1px solid ${C.border}`:"none",fontSize:12}}>
                          <span style={{color:C.text}}>{item.rawLabel}</span>
                          <span style={{color:C.muted}}>{item.stream} · {item.amount!=null?`$${item.amount.toLocaleString()}`:"—"}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{display:"flex",justifyContent:"space-between"}}>
                    <Btn variant="ghost" onClick={cancelReview}>Cancel — Don't Save</Btn>
                    <Btn onClick={finishReview}>Save to Historical Data →</Btn>
                  </div>
                </>
              );
            })()}
          </div>
        ):(
          <>
            <div style={{marginBottom:14}}>
              <label style={labelStyle}>Waste Account</label>
              {wasteAccounts.length===0?(
                <div style={{fontSize:12,color:C.muted}}>No waste accounts set up yet. <button onClick={()=>setScreen("waste-accounts")} style={{color:C.teal,background:"none",border:"none",cursor:"pointer",fontWeight:600,fontSize:12}}>Add one →</button></div>
              ):(
                <select style={inputStyle} value={selectedAccountId} onChange={e=>setSelectedAccountId(e.target.value)}>
                  <option value="">Select an account…</option>
                  {wasteAccounts.map(a=><option key={a.id} value={a.id}>{a.name} ({a.haulerName})</option>)}
                </select>
              )}
            </div>
            <div onClick={()=>selectedAccountId&&!invoiceUploading&&invoiceFileRef.current.click()}
              style={{border:`2px dashed ${selectedAccountId?C.teal:C.border}`,borderRadius:10,padding:"28px 24px",textAlign:"center",cursor:selectedAccountId?"pointer":"not-allowed",background:selectedAccountId?C.tealLight:C.sand,opacity:selectedAccountId?1:0.6,transition:"all 0.2s"}}>
              <div style={{fontSize:26,marginBottom:8}}>{invoiceUploading?"⏳":"📄"}</div>
              <div style={{color:selectedAccountId?C.teal:C.muted,fontWeight:600,fontSize:14}}>
                {invoiceUploading?"Reading invoice…":selectedAccountId?"Click to upload invoice PDF":"Select an account first"}
              </div>
              <div style={{color:C.muted,fontSize:12,marginTop:4}}>PDF only</div>
            </div>
            <input ref={invoiceFileRef} type="file" accept=".pdf,application/pdf" style={{display:"none"}}
              onChange={e=>{if(e.target.files[0])handleInvoiceFile(e.target.files[0]);e.target.value="";}}/>
            {invoiceError&&<div style={{marginTop:12,fontSize:12,color:C.red}}>⚠ {invoiceError}</div>}
          </>
        )}
      </Card>

      {/* ── Admin: Historical Data (CSV) — secondary, setup-only ─── */}
      <Card style={{marginBottom:20,background:C.sand,border:`1px dashed ${C.border}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
          <div>
            <div style={{fontWeight:700,color:C.muted,fontSize:11,letterSpacing:0.5,textTransform:"uppercase",marginBottom:4}}>Admin — For Setup</div>
            <div style={{fontWeight:600,color:C.navy,fontSize:14}}>Upload Historical Data (CSV)</div>
            <p style={{fontSize:12,color:C.muted,marginTop:4,marginBottom:0,lineHeight:1.5}}>Bulk-load past months in the standardized CSV format. Intended for initial setup — day-to-day invoices should use the upload above.</p>
          </div>
          <Btn variant="secondary" onClick={()=>wasteFileRef.current.click()} disabled={wasteUploading} style={{fontSize:12,flexShrink:0}}>{wasteUploading?"Uploading…":"Upload CSV"}</Btn>
          <input ref={wasteFileRef} type="file" accept=".csv" style={{display:"none"}}
            onChange={e=>{if(e.target.files[0])handleWasteUpload(e.target.files[0]);e.target.value="";}}/>
        </div>
        <div style={{marginTop:10,fontSize:11,color:C.muted}}>
          Expected columns: <code style={{background:C.white,padding:"2px 6px",borderRadius:4}}>date,location_name,stream,volume_cubic_yards,weight_tons,weight_lbs,cost_dollars</code>
        </div>
      </Card>

      {(wasteUploadErrors.length>0||wasteUploadSuccess)&&(
        <Card style={{marginBottom:20,background:wasteUploadErrors.length>0?C.redLight:C.greenLight,border:`1px solid ${wasteUploadErrors.length>0?C.red:C.green}33`}}>
          {wasteUploadErrors.length>0?(
            <>
              <div style={{fontWeight:700,color:C.red,fontSize:13,marginBottom:8}}>Upload failed — {wasteUploadErrors.length} issue{wasteUploadErrors.length!==1?"s":""} found. Nothing was saved.</div>
              <div style={{maxHeight:180,overflowY:"auto"}}>
                {wasteUploadErrors.map((e,i)=><div key={i} style={{fontSize:12,color:C.text,padding:"3px 0"}}>• {e}</div>)}
              </div>
            </>
          ):(
            <div style={{fontWeight:600,color:C.green,fontSize:13}}>✓ {wasteUploadSuccess}</div>
          )}
        </Card>
      )}

      {wasteData.length===0?(
        <Card style={{textAlign:"center",padding:"32px 24px",border:`2px dashed ${C.border}`}}>
          <div style={{fontSize:28,marginBottom:10}}>📈</div>
          <div style={{fontWeight:700,color:C.navy,fontSize:15,marginBottom:6}}>No waste data yet</div>
          <p style={{color:C.muted,fontSize:13,margin:0,lineHeight:1.6}}>
            Upload an invoice above, or ask your admin to bulk-load historical data, to see cost and volume/weight trends across all locations over time.
          </p>
        </Card>
      ):(
        <>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
            <span style={{fontSize:12,fontWeight:600,color:C.muted}}>Fiscal Year</span>
            <select style={{...inputStyle,width:"auto",minWidth:110,padding:"7px 12px"}} value={activeWasteYear||""} onChange={e=>setSelectedWasteYear(e.target.value)}>
              {wasteYears.map(y=><option key={y} value={y}>{y}</option>)}
            </select>
          </div>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:12,marginBottom:20}}>
            <Card style={{padding:16}}>
              <div style={{fontSize:11,color:C.muted,fontWeight:700,marginBottom:6}}>TOTAL ANNUAL COST</div>
              <div style={{fontSize:20,fontWeight:800,color:C.navy}}>{fmtMoney(wasteTotalCostYear)}</div>
            </Card>
            <Card style={{padding:16}}>
              <div style={{fontSize:11,color:C.muted,fontWeight:700,marginBottom:6}}>{wasteHasMetric?`TOTAL ${wasteMetricLabel.toUpperCase()}`:"VOLUME / WEIGHT"}</div>
              <div style={{fontSize:20,fontWeight:800,color:C.teal}}>{wasteHasMetric?fmtMetric(wasteTotalMetricYear,wasteMetricUnit):"Not configured"}</div>
            </Card>
            <Card style={{padding:16}}>
              <div style={{fontSize:11,color:C.muted,fontWeight:700,marginBottom:6}}>TOP GENERATING LOCATION</div>
              <div style={{fontSize:16,fontWeight:800,color:C.navy,lineHeight:1.3}}>{topWasteLocation?topWasteLocation.name:"—"}</div>
              {topWasteLocation&&wasteHasMetric&&<div style={{fontSize:11,color:C.muted,marginTop:2}}>{fmtMetric(topWasteLocation.metric,wasteMetricUnit)}</div>}
            </Card>
            <Card style={{padding:16}}>
              <div style={{fontSize:11,color:C.muted,fontWeight:700,marginBottom:6}}>YOY COST CHANGE</div>
              <div style={{fontSize:20,fontWeight:800,color:wasteYoyChange==null?C.muted:wasteYoyChange>0?C.red:C.green}}>
                {wasteYoyChange==null?"N/A":`${wasteYoyChange>0?"+":""}${wasteYoyChange.toFixed(1)}%`}
              </div>
              {prevWasteYear&&wasteTotalCostPrevYear>0&&<div style={{fontSize:11,color:C.muted,marginTop:2}}>vs FY {prevWasteYear}</div>}
            </Card>
          </div>

          <Card style={{marginBottom:16}}>
            <div style={{fontWeight:700,color:C.navy,fontSize:14,marginBottom:2}}>Monthly Cost Trend — All Locations</div>
            <div style={{fontSize:12,color:C.muted,marginBottom:8}}>Fiscal Year {activeWasteYear}</div>
            <LineChart data={wasteMonthly.map(m=>({label:m.label,value:m.cost}))} color={C.navy} isMoney/>
          </Card>

          {wasteHasMetric&&(
            <Card style={{marginBottom:20}}>
              <div style={{fontWeight:700,color:C.navy,fontSize:14,marginBottom:2}}>Monthly {wasteMetricLabel} Trend — All Locations</div>
              <div style={{fontSize:12,color:C.muted,marginBottom:8}}>Fiscal Year {activeWasteYear}</div>
              <BarChart data={wasteMonthly.map(m=>({label:m.label,value:m.metric}))} color={C.teal} unit={wasteMetricUnit}/>
            </Card>
          )}

          <Card style={{marginBottom:20}}>
            <div style={{fontWeight:700,color:C.navy,fontSize:14,marginBottom:14}}>Location Breakdown — Fiscal Year {activeWasteYear}</div>
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                <thead>
                  <tr style={{borderBottom:`2px solid ${C.border}`}}>
                    <th style={{textAlign:"left",padding:"6px 10px",color:C.muted,fontWeight:700,fontSize:11}}>LOCATION</th>
                    <th style={{textAlign:"right",padding:"6px 10px",color:C.muted,fontWeight:700,fontSize:11}}>ANNUAL COST</th>
                    <th style={{textAlign:"right",padding:"6px 10px",color:C.muted,fontWeight:700,fontSize:11}}>{wasteHasMetric?wasteMetricLabel.toUpperCase():"VOLUME/WEIGHT"}</th>
                  </tr>
                </thead>
                <tbody>
                  {wasteLocationRows.map((l,i)=>(
                    <tr key={l.name} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?C.white:C.sand}}>
                      <td style={{padding:"8px 10px",fontWeight:600,color:C.navy}}>{l.name}</td>
                      <td style={{padding:"8px 10px",textAlign:"right",color:C.text}}>{fmtMoney(l.cost)}</td>
                      <td style={{padding:"8px 10px",textAlign:"right",color:C.text}}>{wasteHasMetric?fmtMetric(l.metric,wasteMetricUnit):"—"}</td>
                    </tr>
                  ))}
                  <tr style={{borderTop:`2px solid ${C.border}`,background:C.tealLight}}>
                    <td style={{padding:"8px 10px",fontWeight:700,color:C.navy}}>Total</td>
                    <td style={{padding:"8px 10px",textAlign:"right",fontWeight:700,color:C.navy}}>{fmtMoney(wasteTotalCostYear)}</td>
                    <td style={{padding:"8px 10px",textAlign:"right",fontWeight:700,color:C.navy}}>{wasteHasMetric?fmtMetric(wasteTotalMetricYear,wasteMetricUnit):"—"}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
